import React from "react";
import { Box, Container, Typography, Chip } from "@mui/material";
import {
  Public,
  Store as StoreIcon,
  LocalOffer,
  Home,
  NavigateNext,
  EventAvailable,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

interface ExploreHeroSectionProps {
  mallsCount: number;
  storesCount: number;
  offersCount: number;
  eventsCount: number;
}

/**
 * Explore Hero Section Component - MallInHand
 *
 * Hero section with deep navy gradient, gold accents, and animated stats.
 * Features modern visual design for all shopping malls across locations.
 */
const ExploreHeroSection: React.FC<ExploreHeroSectionProps> = ({
  mallsCount,
  storesCount,
  offersCount,
  eventsCount,
}) => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        background:
          "linear-gradient(135deg, #1a237e 0%, #0d47a1 50%, #1a237e 100%)", // Premium navy - consistent with other screens
        color: "white",
        pt: { xs: 2, md: 3 },
        pb: { xs: 4, md: 5 },
        position: "relative",
        overflow: "hidden",
        zIndex: 1000,
        boxShadow: "0 4px 20px rgba(26, 35, 126, 0.3)",
      }}
    >
      {/* Gold accent pattern */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.08,
          backgroundImage:
            "radial-gradient(circle, rgba(212, 175, 55, 0.4) 1px, transparent 1px)",
          backgroundSize: "20px 20px",
        }}
      />

      {/* Animated overlay */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "radial-gradient(circle at 20% 50%, rgba(212, 175, 55, 0.15) 0%, transparent 50%)",
          animation: "pulse 4s ease-in-out infinite",
          "@keyframes pulse": {
            "0%, 100%": { opacity: 0.5 },
            "50%": { opacity: 1 },
          },
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Breadcrumb Navigation */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: { xs: 2, md: 2.5 },
            }}
          >
            <Home
              sx={{ fontSize: 20, cursor: "pointer" }}
              onClick={() => navigate("/")}
            />
            <NavigateNext sx={{ fontSize: 20 }} />
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Explore
            </Typography>
          </Box>

          {/* Title and Subtitle */}
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 1.5,
                fontSize: { xs: "1.25rem", sm: "2rem", md: "2.5rem" },
                letterSpacing: { xs: "-0.5px", md: "-1px" },
              }}
            >
              Explore Your Favorite Malls -Digitally
            </Typography>
            <Typography
              variant="h6"
              sx={{
                opacity: 0.95,
                fontWeight: 400,
                fontSize: { xs: "0.95rem", sm: "1.1rem", md: "1.2rem" },
                maxWidth: 650,
                mx: "auto",
                mb: 3.5,
                lineHeight: 1.7,
              }}
            >
              Discover brands, stores, offers, events and buy parking ticket
              instantly on WhatsApp.
            </Typography>
          </Box>

          {/* Stats Chips */}
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2, md: 3 },
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Chip
              icon={<Public />}
              label={`${mallsCount} Mall${mallsCount !== 1 ? "s" : ""}`}
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                color: "white",
                fontWeight: 600,
                fontSize: { xs: "0.875rem", sm: "1rem" },
                px: { xs: 1, sm: 1.5 },
                "& .MuiChip-icon": { color: "white" },
              }}
            />
            <Chip
              icon={<StoreIcon />}
              label={`${storesCount} Stores`}
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                color: "white",
                fontWeight: 600,
                fontSize: { xs: "0.875rem", sm: "1rem" },
                px: { xs: 1, sm: 1.5 },
                "& .MuiChip-icon": { color: "white" },
              }}
            />
            <Chip
              icon={<LocalOffer />}
              label={`${offersCount} Offer${offersCount !== 1 ? "s" : ""}`}
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                color: "white",
                fontWeight: 600,
                fontSize: { xs: "0.875rem", sm: "1rem" },
                px: { xs: 1, sm: 1.5 },
                "& .MuiChip-icon": { color: "white" },
              }}
            />
            <Chip
              icon={<EventAvailable />}
              label={`${eventsCount} Event${eventsCount !== 1 ? "s" : ""}`}
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                color: "white",
                fontWeight: 600,
                fontSize: { xs: "0.875rem", sm: "1rem" },
                px: { xs: 1, sm: 1.5 },
                "& .MuiChip-icon": { color: "white" },
              }}
            />
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default ExploreHeroSection;
